import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Database, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button, Card, Field, Input, Pill } from '../../components/ui';

export interface StorageView {
  driver: 'local' | 's3' | 'bunny';
  local: { dir: string };
  s3: {
    endpoint: string;
    region: string;
    bucket: string;
    accessKeyId: string;
    secretAccessKey: string;
    forcePathStyle: boolean;
  };
  bunny: { host: string; zone: string; accessKey: string };
  configured: { s3: boolean; bunny: boolean };
  defaultLocalDir: string;
}

/**
 * File-storage credentials, on the API keys page beside the payment gateway
 * ones (the operator's call): every secret the platform holds is managed in
 * one place. The bucket, region, endpoint and driver are NOT secrets and
 * stay on Settings → Main, where the rest of the platform's configuration
 * lives — this card says so rather than duplicating them.
 *
 * Same discipline as the gateway card: values stay in their fields across
 * refreshes, secrets render masked with a local Show/Mask, each provider
 * saves only its own fields so a browser autofill cannot cross-write.
 */
export function StorageCredentialsCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['storage', 'settings'],
    queryFn: () => api.get<StorageView>('/storage/settings'),
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [unmasked, setUnmasked] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const seeded = useRef(false);
  useEffect(() => {
    if (!query.data || seeded.current) return;
    seeded.current = true;
    const d = query.data;
    setForm({
      s3AccessKeyId: d.s3.accessKeyId,
      s3SecretAccessKey: d.s3.secretAccessKey,
      bunnyAccessKey: d.bunny.accessKey,
    });
  }, [query.data]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put('/storage/settings', body),
    onSuccess: async () => {
      setError(null);
      setNotice('Saved.');
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['storage'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const s = query.data;
  if (!s) return null;
  const set = (k: string) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  // Each provider writes ONLY its own fields — the same protection the
  // gateway sections carry, for the same reason.
  const FIELDS = {
    s3: ['s3AccessKeyId', 's3SecretAccessKey'],
    bunny: ['bunnyAccessKey'],
  } as const;
  const saveProvider = (provider: keyof typeof FIELDS) => {
    const body: Record<string, unknown> = {};
    for (const k of FIELDS[provider]) {
      const v = form[k];
      if (v !== undefined && v !== '') body[k] = v;
    }
    setSaving(provider);
    save.mutate(body, { onSettled: () => setSaving(null) });
  };

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Database className="size-4 text-muted" />
          File storage credentials
        </span>
      }
      action={
        <Button
          size="sm"
          variant="ghost"
          icon={unmasked ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          onClick={() => setUnmasked((v) => !v)}
        >
          {unmasked ? 'Mask values' : 'Show values'}
        </Button>
      }
    >
      <p className="border-b border-line px-5 py-3 text-sm text-muted">
        Keys for the object storage the platform keeps firmware and uploads in. Which driver,
        which bucket, which region — none of them secrets — stay on{' '}
        <Link className="text-primary hover:underline" to="/settings">
          Settings → File storage
        </Link>
        , along with the button that tests the store.
      </p>

      <ProviderSection
        name="S3-compatible"
        note="AWS, MinIO, Vultr Object Storage, Cloudflare R2"
        configured={s.configured.s3}
        active={s.driver === 's3'}
        onSave={() => saveProvider('s3')}
        saving={save.isPending && saving === 's3'}
      >
        <CredField label="Access key id" k="s3AccessKeyId" form={form} set={set} unmasked={unmasked} />
        <CredField label="Secret access key" k="s3SecretAccessKey" form={form} set={set} secret unmasked={unmasked} />
      </ProviderSection>

      <ProviderSection
        name="bunny.net"
        note="Edge Storage — the zone's password"
        configured={s.configured.bunny}
        active={s.driver === 'bunny'}
        onSave={() => saveProvider('bunny')}
        saving={save.isPending && saving === 'bunny'}
      >
        <CredField label="Access key" k="bunnyAccessKey" form={form} set={set} secret unmasked={unmasked} />
      </ProviderSection>

      {error && <p className="px-5 py-3 text-sm text-danger">{error}</p>}
      {notice && <p className="px-5 py-3 text-sm text-success">{notice}</p>}
    </Card>
  );
}

function ProviderSection({
  name,
  note,
  configured,
  active,
  onSave,
  saving,
  children,
}: {
  name: string;
  note: string;
  configured: boolean;
  active: boolean;
  onSave: () => void;
  saving: boolean;
  children: ReactNode;
}) {
  return (
    <div className="border-b border-line px-5 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="text-sm font-semibold text-ink">{name}</p>
        <Pill state tone={configured ? 'success' : 'neutral'}>
          {configured ? 'configured' : 'not configured'}
        </Pill>
        {active && <Pill tone="info">in use</Pill>}
        <span className="text-xs text-faint">{note}</span>
      </div>
      <div className="grid gap-4">{children}</div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" variant="primary" loading={saving} onClick={onSave}>
          Save {name.split(/[ -]/)[0]}
        </Button>
      </div>
    </div>
  );
}

function CredField({
  label,
  k,
  form,
  set,
  secret,
  unmasked,
}: {
  label: string;
  k: string;
  form: Record<string, string>;
  set: (k: string) => (e: { target: { value: string } }) => void;
  secret?: boolean;
  unmasked?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const value = form[k] ?? '';
  return (
    <div className="relative">
      <Field label={label}>
        <Input
          type={secret && !unmasked ? 'password' : 'text'}
          name={`st-${k}`}
          value={value}
          onChange={set(k)}
          placeholder="hidden — type to replace"
          className="font-mono pr-16"
          autoComplete={secret ? 'new-password' : 'off'}
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
        />
      </Field>
      {value && (
        <button
          type="button"
          className="absolute bottom-2 right-2.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          <Copy className="size-3" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}
